import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../api/firebase';
import { WebGLShader } from '../../components/ui/web-gl-shader';
import { LiquidButton } from '../../components/ui/liquid-glass-button';
import { TextColor } from '../../components/ui/text-color';
import { LandingAccordionItem } from '../../components/ui/interactive-image-accordion';
import ProductShowcase from '../../components/ui/product-showcase';
import ArenaCarousel from '../../components/ui/arena-carousel';
import BrazilMap from '../../components/ui/brazil-map';
import TestimonialsSection from '../../components/ui/testimonials-section';
import ArenaiPromoSection from '../../components/ui/arenai-promo-section';
import { StickyFooter } from '../../components/ui/sticky-footer';
import { MenuBar } from '../../components/ui/glow-menu';
import { isAdminUser } from '../../api/gestor';
import { Home, Video, Handshake, Radio, LogIn, ShieldCheck, Trophy, CircleDot, LayoutDashboard } from 'lucide-react';
import womanPlayingVolley from '../../assets/images/main-banner.webp';
import logoImg from '../../assets/images/logo.svg';

const gestorMenuItem = {
  icon: LayoutDashboard, label: 'Gestor', href: '/gestor',
  gradient: 'radial-gradient(circle, rgba(126,226,63,0.2) 0%, rgba(126,226,63,0.06) 50%, transparent 100%)',
  iconColor: 'text-green-500',
};

const menuItems = [
  {
    icon: Home,
    label: 'Início',
    href: '/',
    gradient:
      'radial-gradient(circle, rgba(245,138,37,0.2) 0%, rgba(229,118,5,0.08) 50%, transparent 100%)',
    iconColor: 'text-orange-500',
  },
  {
    icon: Video,
    label: 'Vídeos',
    href: '/videos',
    gradient:
      'radial-gradient(circle, rgba(59,130,246,0.15) 0%, rgba(37,99,235,0.06) 50%, transparent 100%)',
    iconColor: 'text-blue-500',
  },
  {
    icon: CircleDot,
    label: 'FastTennis',
    href: '/fasttennis',
    gradient:
      'radial-gradient(circle, rgba(43,76,255,0.25) 0%, rgba(43,76,255,0.08) 50%, transparent 100%)',
    iconColor: 'text-blue-400',
  },
  {
    icon: Radio,
    label: 'Ao Vivo',
    href: '/livestream',
    gradient:
      'radial-gradient(circle, rgba(239,68,68,0.15) 0%, rgba(220,38,38,0.06) 50%, transparent 100%)',
    iconColor: 'text-red-500',
  },
  {
    icon: ShieldCheck,
    label: 'FutSafe',
    href: '/futsafe',
    gradient:
      'radial-gradient(circle, rgba(234,179,8,0.2) 0%, transparent 100%)',
    iconColor: 'text-yellow-500',
  },
  {
    icon: Trophy,
    label: 'Torneios',
    href: '/torneios',
    gradient:
      'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 100%)',
    iconColor: 'text-purple-500',
  },
  {
    icon: Handshake,
    label: 'Contratar',
    href: '/contract-camera',
    gradient:
      'radial-gradient(circle, rgba(34,197,94,0.15) 0%, transparent 100%)',
    iconColor: 'text-green-500',
  },
  {
    icon: LogIn,
    label: 'Entrar',
    href: '/login',
    gradient:
      'radial-gradient(circle, rgba(168,85,247,0.15) 0%, transparent 100%)',
    iconColor: 'text-purple-500',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('accessToken'));
  const [userEmail, setUserEmail] = useState<string | null>(auth.currentUser?.email ?? null);

  useEffect(() => {
    return onAuthStateChanged(auth, (user) => {
      setUserEmail(user?.email ?? null);
      if (user) {
        user.getIdToken().then((token) => {
          localStorage.setItem('accessToken', token);
          setIsLoggedIn(true);
        });
      } else {
        localStorage.removeItem('accessToken');
        setIsLoggedIn(false);
      }
    });
  }, []);

  const goToVideosOrLogin = () => navigate(isLoggedIn ? '/videos' : '/login');

  const navItems = isAdminUser(userEmail) ? [...menuItems, gestorMenuItem] : menuItems;

  const handleMenuClick = (label: string) => {
    const item = navItems.find((i) => i.label === label);
    if (item) navigate(item.href);
  };

  return (
    <div className="dark-section" style={{ background: '#0a0a0a' }}>
      {/* Floating Glow Menu Navbar */}
      <header className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-6 lg:px-12 py-3 backdrop-blur-xl border-b border-white/5" style={{ backgroundColor: 'rgba(10,10,10,0.8)' }}>
        <img src={logoImg} alt="Apertai" className="hidden md:block" style={{ width: '127px' }} />
        <MenuBar
          items={navItems}
          activeItem="Início"
          onItemClick={handleMenuClick}
        />
      </header>

      {/* ===== HERO SECTION ===== */}
      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* WebGL Background */}
        <div className="absolute inset-0 opacity-40">
          <WebGLShader />
        </div>

        {/* Dark overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/90" />

        {/* Content — centered */}
        <div className="relative z-10 w-full max-w-3xl mx-auto px-6 text-center pt-28 md:pt-32">
          {/* NOVO eyebrow chip */}
          <div
            className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-full mb-6 border"
            style={{
              borderColor: 'rgba(255,255,255,0.18)',
              background: 'rgba(255,255,255,0.04)',
              fontFamily: 'JetBrains Mono, monospace',
              fontSize: 11,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: '#9a9a9a',
            }}
          >
            <span
              style={{
                width: 6, height: 6, borderRadius: '50%',
                background: '#6f8dff', boxShadow: '0 0 10px #2b4cff',
              }}
            />
            NOVO · <b style={{ color: '#6f8dff', fontWeight: 700 }}>FastTennis Replays</b> ao seu alcance
          </div>

          <h1 style={{ fontSize: 'clamp(3rem, 7vw, 5rem)', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1.05, marginBottom: '1.5rem', color: 'white' }}>
            Seus vídeos em
            <span
              style={{
                display: 'block',
                fontSize: 'inherit',
                fontWeight: 'inherit',
                backgroundImage: 'linear-gradient(135deg, #e57605, #f29b2a)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              alta qualidade
            </span>
          </h1>

          <p className="text-white/50 text-base md:text-lg max-w-lg mx-auto mb-10">
            Câmeras inteligentes que gravam, processam e entregam os melhores lances
            direto no seu celular. Sem complicação.
          </p>

          <div className="flex flex-wrap flex-row items-center justify-center gap-3">
            <button
              onClick={goToVideosOrLogin}
              className="font-semibold px-5 py-2.5 md:px-8 md:py-3 rounded-full text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-105 border-none cursor-pointer text-sm md:text-base"
              style={{ background: 'linear-gradient(135deg, #e57605, #f29b2a)' }}
            >
              {isLoggedIn ? 'Ver meus vídeos' : 'Entrar'}
            </button>

            {/* FastTennis CTA — blue identity */}
            <button
              onClick={() => navigate(isLoggedIn ? '/fasttennis' : '/login')}
              className="relative font-bold rounded-full text-white border-none cursor-pointer transition-all duration-300 hover:shadow-xl hover:scale-105"
              style={{
                background: 'linear-gradient(95deg, #0e1b6b 0%, #2b4cff 70%, #6f8dff 100%)',
                padding: '10px 18px 10px 10px',
                fontSize: '13px',
                boxShadow: '0 14px 32px -12px rgba(43,76,255,0.6)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                whiteSpace: 'nowrap',
                lineHeight: 1.1,
              }}
            >
              <span
                aria-hidden
                style={{
                  position: 'absolute',
                  top: -2, right: -2,
                  width: 10, height: 10, borderRadius: '50%',
                  background: '#6f8dff',
                  boxShadow: '0 0 0 3px #0a0a0a, 0 0 14px #2b4cff',
                  animation: 'apertaiFtPulse 1.6s infinite',
                }}
              />
              <span
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '4px 8px', borderRadius: 999,
                  background: 'rgba(255,255,255,0.96)', color: '#0e1b6b',
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: 10, fontWeight: 800,
                  letterSpacing: '0.1em', textTransform: 'uppercase',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0e1b6b" strokeWidth="3" strokeLinecap="round">
                  <path d="M4 14c4-6 12-6 16 0M6 18c3-4 9-4 12 0" />
                </svg>
                FT
              </span>
              Replays FastTennis
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M5 12h14" />
                <path d="M13 5l7 7-7 7" />
              </svg>
            </button>

            <LiquidButton
              className="text-white border border-white/20 rounded-full"
              size="lg"
              onClick={() => navigate('/contract-camera')}
            >
              Contratar câmera
            </LiquidButton>
          </div>

          {/* Pulse keyframes for the NEW dot */}
          <style>{`
            @keyframes apertaiFtPulse {
              0%, 100% { box-shadow: 0 0 0 3px #0a0a0a, 0 0 14px #2b4cff; }
              50%      { box-shadow: 0 0 0 3px #0a0a0a, 0 0 22px #6f8dff; }
            }
          `}</style>
        </div>

        {/* TextColor animated words */}
        <div className="relative z-10 mt-12 md:mt-16 w-full mx-auto px-6 lg:px-16">
          <TextColor words={['Grave.', 'Assista.', 'Compartilhe.']} />
        </div>

        {/* Scroll indicator */}
        <div className="relative z-10 mt-8 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* ===== ARENA CAROUSEL ===== */}
      <ArenaCarousel />

      {/* ===== BRAZIL MAP ===== */}
      <BrazilMap />

      {/* ===== FEATURES ACCORDION ===== */}
      <LandingAccordionItem />

      {/* ===== PRODUCT SHOWCASE (Camera + Central) ===== */}
      <ProductShowcase />

      {/* ===== ARENAI PROMO ===== */}
      <ArenaiPromoSection />

      {/* ===== TESTIMONIALS ===== */}
      <TestimonialsSection />

      {/* ===== CTA FINAL ===== */}
      <section
        className="relative py-20 md:py-32 overflow-hidden"
        style={{ background: 'linear-gradient(135deg, #e57605 0%, #f29b2a 100%)' }}
      >
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>
        <div className="relative z-10 container mx-auto px-4 max-w-4xl text-center">
          <h2 className="text-3xl md:text-5xl font-bold text-white tracking-tight mb-6">
            Pronto para transformar sua arena?
          </h2>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10">
            Instalação gratuita, suporte 24/7 e sem taxas escondidas.
            Comece hoje e veja a diferença.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="/contract-camera"
              className="inline-block bg-white font-bold px-10 py-4 rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 text-base no-underline"
              style={{ color: '#e57605' }}
            >
              Contratar Agora
            </a>
            <a
              href={isLoggedIn ? '/videos' : '/login'}
              className="inline-block bg-transparent border-2 border-white text-white font-bold px-10 py-4 rounded-lg hover:bg-white/10 transition-all duration-300 text-base no-underline"
            >
              {isLoggedIn ? 'Ver meus vídeos' : 'Já sou cliente'}
            </a>
          </div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <StickyFooter />
    </div>
  );
}
